/**
 * Navigation on a phone.
 *
 * The sidebar is 236px and never collapsed, which on a 402px screen took 58% of
 * the width before any content existed. It is hidden below `lg` now and this takes
 * over: a bottom bar, because the app is an installed `standalone` PWA in portrait
 * and the bottom of the screen is the part a thumb reaches.
 *
 * Three tabs and a More sheet rather than all seven across 402px, which would put
 * each under the 44px tap target once padding is counted. Nothing is unreachable;
 * it is one tap further away.
 *
 * `position: fixed` is safe here specifically because this renders as a sibling of
 * `<main>`, not inside it. `<main>` carries the `.kete-enter` transform, and a
 * transform makes an ancestor a containing block for fixed positioning — the same
 * trap that sends sheets to the middle of a register several screens tall, which is
 * why those go through `lib/Portal.tsx`. Nothing here is inside that subtree.
 */

import { useState } from 'react'
import { useAuth } from '../lib/auth.tsx'
import type { Screen } from '../screens.ts'
import { SCREENS } from '../screens.ts'

/** The phone's own shortlist, in the order a thumb should find them. */
const PRIMARY: readonly Screen[] = ['ahead', 'fill', 'ask']

interface MobileNavProps {
  screen: Screen
  onNavigate: (screen: Screen) => void
  badges: Partial<Record<Screen, string>>
}

export function MobileNav({ screen, onNavigate, badges }: MobileNavProps) {
  const { signedIn, signOut } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)

  const primary = PRIMARY.map((id) => SCREENS.find((s) => s.id === id)).filter(
    (s): s is (typeof SCREENS)[number] => s !== undefined,
  )
  const rest = SCREENS.filter((s) => !PRIMARY.includes(s.id))
  const inMore = rest.some((s) => s.id === screen)

  function go(next: Screen) {
    setMoreOpen(false)
    onNavigate(next)
  }

  return (
    <>
      {moreOpen && (
        // Dismiss by tapping away, the way a sheet should. Not a button, because a
        // full-screen button is announced as one; this is a backdrop.
        <div
          className="fixed inset-0 z-30 bg-ink/20 lg:hidden"
          onClick={() => setMoreOpen(false)}
          aria-hidden="true"
        />
      )}

      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex flex-col border-t border-line bg-sand lg:hidden"
        // The home indicator sits over the bottom of the screen on a notched
        // phone, and `viewport-fit=cover` is already set in index.html, so the bar
        // has to pad itself out of the way. `max()` keeps a sane floor on a device
        // that reports no inset at all.
        style={{ paddingBottom: 'max(6px, env(safe-area-inset-bottom))' }}
      >
        {moreOpen && (
          <div className="flex flex-col border-b border-line-warm px-2 py-2">
            {rest.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => go(item.id)}
                aria-current={item.id === screen ? 'page' : undefined}
                className={`flex min-h-11 items-center justify-between rounded-lg px-3 text-left text-[15px] ${
                  item.id === screen ? 'bg-card font-medium' : 'font-light text-ink'
                }`}
              >
                <span>{item.label}</span>
                {badges[item.id] && (
                  <span className="text-[12px] text-muted">{badges[item.id]}</span>
                )}
              </button>
            ))}
            {/*
              Sign out at the foot of More, because on a phone there is no sidebar and
              this sheet is the only place that holds everything else. Set apart by a
              rule and muted, so it reads as the end of the list rather than another
              screen to visit.
            */}
            {signedIn && (
              <button
                type="button"
                onClick={signOut}
                className="mt-1 flex min-h-11 items-center rounded-lg border-line-warm border-t px-3 text-left font-light text-[15px] text-muted"
              >
                Sign out
              </button>
            )}
          </div>
        )}

        <div className="flex items-stretch">
          {primary.map((item) => (
            <Tab
              key={item.id}
              label={item.label}
              badge={badges[item.id]}
              active={item.id === screen && !moreOpen}
              onClick={() => go(item.id)}
            />
          ))}
          <Tab
            label="More"
            active={moreOpen || inMore}
            expanded={moreOpen}
            onClick={() => setMoreOpen((open) => !open)}
          />
        </div>
      </nav>
    </>
  )
}

function Tab({
  label,
  badge,
  active,
  expanded,
  onClick,
}: {
  label: string
  badge?: string | undefined
  active: boolean
  expanded?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      {...(expanded === undefined ? {} : { 'aria-expanded': expanded })}
      // min-h-14 rather than min-h-11: 44px is the floor for a target you aim at,
      // and this one is aimed at with a thumb while walking.
      className={`flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 px-1 text-[11px] transition-colors ${
        active ? 'font-medium text-ink' : 'font-light text-muted'
      }`}
    >
      <span className="truncate">{label}</span>
      {/*
        The first word, not the phrase: "3 to review" becomes "3" and "$1,240" stays
        whole. The sidebar has 236px for a phrase; a tab has about 90px.

        `blush-text` and not `on-member`: both are "text on a coloured fill", but
        on-member is fixed dark because an avatar's fill comes from the database and
        is pale in both themes, whereas blush inverts — #ffcacd light, #5e353a dark.
        Pairing the fixed one with the inverting one put dark ink on dark maroon and
        the count could not be read at all. These two flip together by design.
      */}
      {badge && (
        <span className="rounded-full bg-blush px-1.5 text-[11px] leading-4 text-blush-text">
          {badge.split(' ')[0]}
        </span>
      )}
      <span
        aria-hidden="true"
        className={`h-[2px] w-5 rounded-full ${active ? 'bg-ink' : 'bg-transparent'}`}
      />
    </button>
  )
}
